// ASCII Image Effect — vanilla port of the brand's Framer component
// (performance-optimised revision: color batching, quantisation, lens, dirty flag, 30fps cap).
//
// Ported 1:1 from the component source: ASCII ramp, Rec.601 luminance, color modes
// (original / tinted / flat) with the same quantisation, the cursor lens with cosine
// falloff, the dirty-flag + 33ms redraw budget, and the mouse smoothing constant (0.1).
//
// The WebGL pass (Three.js r128 from a CDN) is NOT loaded — this theme ships no external
// assets. Its per-vertex wave and RGB-split shader are replaced with what CSS can do
// natively: the mesh tilt (rotation.y = x*0.4*depth/10) becomes a perspective transform on
// the canvas, and uHue becomes a hue-rotate() filter. The lens — the visible interaction —
// runs in the 2D canvas exactly like the source.

const ASCII =
    ' .`^",:;Il!i><~+_-?][}{1)(|/tfjrxnuvczXYUJCLQ0OZmwqpdbkhao*#MW&8%B@$';
const ASCII_LEN = ASCII.length - 1;
const ASCII_FRAME_MS = 33; // ~30fps redraw budget, same as the source

function parseColor(css) {
    const fc = document.createElement('canvas');
    fc.width = fc.height = 1;
    const ctx = fc.getContext('2d');
    ctx.fillStyle = css;
    ctx.fillRect(0, 0, 1, 1);
    const d = ctx.getImageData(0, 0, 1, 1).data;
    return [d[0], d[1], d[2]];
}

function sampleImage(img, cols, rows) {
    const tmp = document.createElement('canvas');
    tmp.width = cols;
    tmp.height = rows;
    const tc = tmp.getContext('2d', { willReadFrequently: true });
    tc.drawImage(img, 0, 0, cols, rows);
    let data;
    try {
        data = tc.getImageData(0, 0, cols, rows).data;
    } catch (e) {
        return null; // tainted canvas (cross-origin without CORS)
    }
    let hasAlpha = false;
    for (let i = 3; i < data.length; i += 4) {
        if (data[i] < 253) { hasAlpha = true; break; }
    }
    return { data, cols, rows, hasAlpha };
}

// Direct port of the component's drawASCII: color batching, quantisation, sqrt-free lens.
function drawASCII(pixels, out, cs, ff, fs, tint, colorMode, mouseX, mouseY, lensRadius, lensStrength) {
    const { data, cols, rows, hasAlpha } = pixels;
    const ctx = out.getContext('2d');

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.font = `${fs}px ${ff}`;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';

    const lensActive = mouseX > -900;
    const lensRadius2 = lensRadius * lensRadius;

    const batches = {};
    const lensItems = [];

    for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
            const i = (row * cols + col) * 4;
            const pr = data[i], pg = data[i + 1], pb = data[i + 2], pa = data[i + 3];
            if (pa < 10) continue;

            const alpha = pa / 255;
            const lum = (pr * 0.299 + pg * 0.587 + pb * 0.114) / 255;
            const char = ASCII[Math.floor(lum * ASCII_LEN)];

            let color;
            if (colorMode === 'original') {
                const qr = (pr >> 2) << 2;
                const qg = (pg >> 2) << 2;
                const qb = (pb >> 2) << 2;
                color = `rgb(${qr},${qg},${qb})`;
            } else if (colorMode === 'tinted') {
                const ql = Math.round(lum * 31) / 31;
                color = `rgb(${Math.round(tint[0] * ql)},${Math.round(tint[1] * ql)},${Math.round(tint[2] * ql)})`;
            } else {
                color = `rgb(${tint[0]},${tint[1]},${tint[2]})`;
            }

            const cellX = (col + 0.5) * cs;
            const cellY = (row + 0.5) * cs;

            let scale = 1;
            if (lensActive) {
                const dx = cellX - mouseX;
                const dy = cellY - mouseY;
                const dist2 = dx * dx + dy * dy;
                if (dist2 < lensRadius2) {
                    const dist = Math.sqrt(dist2);
                    const falloff = (1 + Math.cos(Math.PI * (dist / lensRadius))) / 2;
                    scale = 1 + (lensStrength - 1) * falloff;
                }
            }

            if (scale > 1.005) {
                lensItems.push({ x: cellX, y: cellY, char, scale, color, alpha });
                continue;
            }

            let b = batches[color];
            if (!b) { b = { xs: [], ys: [], chars: [] }; batches[color] = b; }
            b.xs.push(cellX);
            b.ys.push(cellY);
            b.chars.push(char);
        }
    }

    if (!hasAlpha) ctx.globalAlpha = 1;
    for (const color in batches) {
        ctx.fillStyle = color;
        const { xs, ys, chars } = batches[color];
        for (let k = 0; k < chars.length; k++) ctx.fillText(chars[k], xs[k], ys[k]);
    }

    for (let k = 0; k < lensItems.length; k++) {
        const it = lensItems[k];
        if (hasAlpha) ctx.globalAlpha = it.alpha;
        ctx.fillStyle = it.color;
        ctx.setTransform(it.scale, 0, 0, it.scale, it.x, it.y);
        ctx.fillText(it.char, 0, 0);
    }

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
}

// Emoji placeholders: rendered into an SVG raster so the sampler treats them like photos.
function emojiSource(glyph) {
    const svg =
        "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 320 320'>" +
        "<rect width='320' height='320' fill='#000'/>" +
        "<text x='160' y='160' font-size='230' text-anchor='middle' " +
        "dominant-baseline='central'>" + glyph + "</text></svg>";
    return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
}

function setup(wrap, reducedMotion) {
    let img = wrap.querySelector('img');
    if (!img && wrap.dataset.asciiGlyph) {
        img = document.createElement('img');
        img.alt = '';
        img.setAttribute('aria-hidden', 'true');
        img.src = emojiSource(wrap.dataset.asciiGlyph);
        wrap.appendChild(img);
    }
    if (!img) return;

    // Component defaults, overridable per instance via data attributes.
    const opts = () => ({
        cs: Number(wrap.dataset.asciiCharSize || 8),
        fs: Number(wrap.dataset.asciiFontSize || 10),
        ff: wrap.dataset.asciiFont || '"Courier New", monospace',
        mode: wrap.dataset.asciiMode || 'original',
        tint: parseColor(wrap.dataset.asciiColor || '#00ff88'),
        depth: Number(wrap.dataset.asciiDepth || 10),
        lensRadius: Number(wrap.dataset.asciiLensRadius || 25),
        lensStrength: Number(wrap.dataset.asciiLensStrength || 3),
    });

    // The canvas is created lazily, only after a successful pixel sample. A cross-origin
    // image without CORS headers taints the sampler; in that case no canvas may remain in
    // the DOM at all — an empty one still occupies layout and breaks the card.
    let canvas = wrap.querySelector('.tih-ascii-canvas');
    const ensureCanvas = () => {
        if (!canvas) {
            canvas = document.createElement('canvas');
            canvas.className = 'tih-ascii-canvas';
            canvas.setAttribute('aria-hidden', 'true');
            wrap.appendChild(canvas);
        }
        return canvas;
    };

    const state = {
        pixels: null,
        dead: false,
        dirty: true,
        lastDraw: 0,
        lastMouse: { x: -999, y: -999 },
        mouse: { x: 0, y: 0, tx: 0, ty: 0, inside: false },
        raf: 0,
        visible: true,
        dpr: 1,
    };

    const paintStatic = () => {
        if (!state.pixels) return;
        const o = opts();
        drawASCII(state.pixels, canvas, o.cs * state.dpr, o.ff, o.fs * state.dpr,
            o.tint, o.mode, -999, -999, 0, 1);
    };

    const resample = (W, H) => {
        const o = opts();
        if (!W || !H || !img.naturalWidth) return;
        const cols = Math.max(1, Math.floor((W * state.dpr) / (o.cs * state.dpr)));
        const rows = Math.max(1, Math.floor((H * state.dpr) / (o.cs * state.dpr)));
        state.pixels = sampleImage(img, cols, rows);
        if (!state.pixels) {
            // Tainted (cross-origin, no CORS): leave the plain image, remove any canvas.
            if (canvas) { canvas.remove(); canvas = null; }
            wrap.classList.remove('is-ascii');
            state.dead = true;
            return;
        }
        state.dirty = true;
        const c = ensureCanvas();
        c.width = W * state.dpr;
        c.height = H * state.dpr;
        wrap.classList.add('is-ascii');
        // First frame is drawn synchronously: rAF does not tick in hidden or
        // background tabs, and the art must not sit black until focus.
        paintStatic();
    };

    const size = () => {
        // Measure the wrap while the flow is settled by the IMG, never by the canvas.
        const W = Math.round(wrap.clientWidth);
        const H = Math.round(wrap.clientHeight || W);
        if (!W || !H) return;
        state.dpr = Math.min(window.devicePixelRatio || 1, 2);
        resample(W, H);
    };

    const frame = (t) => {
        if (state.dead) return;
        state.raf = requestAnimationFrame(frame);
        if (!state.visible || !state.pixels || !canvas) return;

        const m = state.mouse;
        m.x += (m.tx - m.x) * 0.1;
        m.y += (m.ty - m.y) * 0.1;

        const o = opts();

        // The source mesh tilt (rotation.y = x*0.4*d, rotation.x = -y*0.28*d, radians)
        // expressed as a CSS perspective transform; hue shift = uHue (radians) in degrees.
        const d = o.depth / 10;
        const ry = m.x * 0.4 * d * 57.3;
        const rx = -m.y * 0.28 * d * 57.3;
        canvas.style.transform = `perspective(80rem) rotateY(${ry.toFixed(2)}deg) rotateX(${rx.toFixed(2)}deg)`;
        canvas.style.filter = m.inside ? `hue-rotate(${(m.tx * 1.2 * 57.3).toFixed(1)}deg)` : '';

        const cW = canvas.width, cH = canvas.height;
        const mx = m.inside ? (m.x * 0.5 + 0.5) * cW : -999;
        const my = m.inside ? (m.y * 0.5 + 0.5) * cH : -999;

        const moved = Math.abs(mx - state.lastMouse.x) > 0.5 || Math.abs(my - state.lastMouse.y) > 0.5;
        if ((state.dirty || moved) && t - state.lastDraw >= ASCII_FRAME_MS) {
            const lensR = (o.lensRadius / 100) * Math.min(cW, cH);
            drawASCII(
                state.pixels, canvas,
                o.cs * state.dpr, o.ff, o.fs * state.dpr,
                o.tint, o.mode,
                mx, my, lensR, o.lensStrength
            );
            state.lastMouse.x = mx;
            state.lastMouse.y = my;
            state.dirty = false;
            state.lastDraw = t;
        }
    };

    const toLocal = (clientX, clientY) => {
        const r = wrap.getBoundingClientRect();
        state.mouse.tx = ((clientX - r.left) / r.width - 0.5) * 2;
        state.mouse.ty = ((clientY - r.top) / r.height - 0.5) * 2;
        state.mouse.inside = true;
    };

    wrap.addEventListener('mousemove', (e) => toLocal(e.clientX, e.clientY));
    wrap.addEventListener('mouseleave', () => {
        state.mouse.tx = 0;
        state.mouse.ty = 0;
        state.mouse.inside = false;
        state.dirty = true; // one redraw to clear the lens
    });
    wrap.addEventListener('touchmove', (e) => {
        if (e.touches[0]) toLocal(e.touches[0].clientX, e.touches[0].clientY);
    }, { passive: true });
    wrap.addEventListener('touchend', () => {
        state.mouse.tx = 0;
        state.mouse.ty = 0;
        state.mouse.inside = false;
        state.dirty = true;
    });

    const start = () => {
        size();
        if (reducedMotion) {
            return; // static paint already happened in resample()
        }
        // Pause the loop offscreen.
        new IntersectionObserver((entries) => {
            state.visible = entries[0].isIntersecting;
        }).observe(wrap);

        let t;
        new ResizeObserver(() => {
            clearTimeout(t);
            t = setTimeout(size, 150);
        }).observe(wrap);

        state.raf = requestAnimationFrame(frame);
    };

    if (img.complete && img.naturalWidth) start();
    else img.addEventListener('load', start, { once: true });
}

export default function asciiImage() {
    const wraps = document.querySelectorAll('.tih-ascii');
    if (!wraps.length) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    wraps.forEach((wrap) => setup(wrap, reduced));
}
